// @ts-strict-ignore
/** @jsx jsx */
/** @jsxFrag null */

import {jsx} from 'panel';

import {SvgIconEl} from 'common/components/svg-icon';
import {SelectEl} from 'common/components/select';
import {CheckboxEl} from 'common/components/checkbox/panel';
import {InputEl} from 'common/components/input/panel';
import {ButtonEl} from 'common/components/button';
import {ButtonSize, ButtonTheme} from 'common/components/button/constants';

import spotIllustration1 from './spot-illustration-1.svg';
import mp2023Logomark from './mp2023-logomark.svg';
import type {StartupsApply} from './index';

const MP2023_LOGOMARK_METADATA = {
  src: mp2023Logomark,
  alt: `Mixpanel logo`,
  width: 140,
  height: 140,
} as const;

export function template(this: StartupsApply) {
  const {
    canUpdateBilling,
    creditRedeemed,
    credited,
    currentPaid,
    eligible,
    eligibleForMigration,
    enrolled,
    hasOtherCredit,
    incubator,
    initialValue,
    loggedIn,
    pastExpiry,
    showCongrats,
    showOther,
  } = this.state;

  const TermsCheckbox = () => (
    <div attrs={{class: `terms-checkbox-field`}}>
      <div attrs={{class: `terms-checkbox`}}>
        <CheckboxEl
          sel='.terms-checkbox-input'
          elref={`required-terms-checkbox`}
          checked={this.state.requiredFields.acceptsTerms}
          onClick={this.helpers?.handleAcceptsTermsClick}
        />
        <label attrs={{for: `required-terms-checkbox`, class: `terms-checkbox-label text--caption-xs`}}>
          I certify that I meet the eligibility criteria of the Mixpanel for Startups Program and understand that my
          participation may be revoked if my eligibility has been misrepresented.
        </label>
      </div>
      {this.state.showAcceptAgreementError && (
        <div attrs={{class: `form-helper-text -with-margin-left -has-error text--caption-xs`}}>
          Agreement is required for signup
        </div>
      )}
    </div>
  );

  const FormFields = ({tfbf}) => (
    <div sel='.auth-form-field-row'>
      <div sel='.referral-partner-container'>
        <h2 attrs={{class: `header-text text--body-sm`}}>How did you hear about us?</h2>
      </div>
      <SelectEl
        attrs={{
          options: this.helpers?.REFERRAL_INCUBATORS,
          'selected-option': incubator,
          'label-transform': `uppercase`,
          tabindex: 0,
          disabled: tfbf,
        }}
        on={{change: this.helpers?.updateReferralIncubator}}
      />
      {showOther && (
        <div sel='.other-input-container'>
          <InputEl
            onInput={this.helpers?.handleOtherInput}
            fieldId={`id_otherIncubator`}
            fieldName={`otherIncubator`}
            label={`Share How You Found Us`}
          />
          {this.state.showRequireReferralError && (
            <div attrs={{class: `form-helper-text -has-error text--caption-xs`}}>Please enter a text response 🙏</div>
          )}
        </div>
      )}
      <TermsCheckbox />
    </div>
  );

  const StartupsApplyLayout = () => (
    <div sel='.startups-apply-layout'>
      <header
        attrs={{class: `layout-container`}}
        sel='#logo-header'
        style={{'margin-top': enrolled ? `102px` : `40px`}}
      >
        <a attrs={{href: `/`, class: `logo-link`}}>
          <img attrs={MP2023_LOGOMARK_METADATA} sel='.logo-asset' /> <h1 sel='.logo-text'>Mixpanel for Startups</h1>
        </a>
      </header>
      {enrolled ? (
        <main sel='.layout-container.success-container'>
          <div sel='.content-wrapper'>
            {showCongrats ? (
              <h1 sel='.text--body-sm'>{`Congratulations!`}</h1>
            ) : (
              <h1 sel='.text--body-sm'>{`You're already in...`}</h1>
            )}
            <div sel='.info-row'>
              <SvgIconEl icon={`checkmark`} className='checkmark-icon' />
              <div sel='.info-text'>
                <p sel='.text--caption-sm'>
                  {this.helpers?.getOrgName() || `Your organization`} joined the Mixpanel for Startups program on{` `}
                  {this.attr(`applied-date`)}.
                </p>
              </div>
            </div>
            <div sel='.info-row'>
              <SvgIconEl icon={`checkmark`} className='checkmark-icon' />
              <div sel='.info-text'>
                <p sel='.text--caption-sm'>
                  If you don't connect your data by {this.attr(`implementation-ddl`)}, you will automatically be removed
                  from the program.
                </p>
              </div>
            </div>
            <div sel='.info-row'>
              <SvgIconEl icon={`checkmark`} className='checkmark-icon' />
              <div sel='.info-text'>
                <p sel='.text--caption-sm'>
                  Your Startup Plan covers up to 1 billion annual events and will be active until{` `}
                  {this.attr(`expiration`)}.
                </p>
              </div>
            </div>
            <ButtonEl
              sel='.success-button'
              attrs={{
                theme: ButtonTheme.Primary,
                size: ButtonSize.Large,
                href: this.helpers?.getOnboardingflowUrl(),
              }}
            >
              Let’s Get Started
              <SvgIconEl icon={`arrow-right`} />
            </ButtonEl>
          </div>
          <div sel='.form-container'>
            <img attrs={{src: spotIllustration1}} />
          </div>
        </main>
      ) : credited && !pastExpiry ? (
        <main sel='.layout-container.success-container'>
          <div sel='.content-wrapper'>
            <h1 sel='.text--body-sm'>{`You're already in...`}</h1>
            <div sel='.info-row'>
              <SvgIconEl icon={`checkmark`} className='checkmark-icon' />
              <div sel='.info-text'>
                <p sel='.text--caption-sm'>Joined the Mixpanel for Startups program on {this.attr(`applied-date`)}.</p>
              </div>
            </div>
            <div sel='.info-row'>
              <SvgIconEl icon={`checkmark`} className='checkmark-icon' />
              <div sel='.info-text'>
                <p sel='.text--caption-sm'>
                  🎉 {this.helpers?.getOrgName()} received ${this.helpers?.commaize(initialValue)} in credits to use in
                  your first year 🎉
                </p>
              </div>
            </div>
            <div sel='.info-row'>
              <SvgIconEl icon={`checkmark`} className='checkmark-icon' />
              <div sel='.info-text'>
                <p sel='.text--caption-sm'>
                  {eligibleForMigration
                    ? `If you don't spend your credits by October 4, 2024, you will automatically transition to the new startup program.`
                    : `Credits and time in the program will expire on ${this.attr(`expiration`)}.`}
                </p>
              </div>
            </div>
            {creditRedeemed ? (
              <ButtonEl
                sel='#plan-builder-button.success-button'
                attrs={{
                  theme: ButtonTheme.Primary,
                  size: ButtonSize.Large,
                }}
                on={{click: this.helpers?.clickPlanDetail}}
              >
                See your plan details
              </ButtonEl>
            ) : (
              <ButtonEl
                sel='#plan-builder-button.success-button'
                attrs={{
                  theme: ButtonTheme.Primary,
                  size: ButtonSize.Large,
                }}
                on={{click: this.helpers?.clickPlanBuilder}}
              >
                Use your credits
              </ButtonEl>
            )}
          </div>
          <div sel='.form-container'>
            <img attrs={{src: spotIllustration1}} />
          </div>
        </main>
      ) : (
        <main sel='.layout-container.startups-form'>
          <div sel='.content-wrapper'>
            <div sel='.program-info'>
              <h2 sel='.details-text.text--body-sm.first'>Why join?</h2>
              <div sel='.info-row'>
                <SvgIconEl icon={`checkmark`} className='checkmark-icon' />
                <div sel='.info-text'>
                  <p sel='.text--caption-sm'>Get real-time insights into your product questions.</p>
                </div>
              </div>
              <div sel='.info-row'>
                <SvgIconEl icon={`checkmark`} className='checkmark-icon' />
                <div sel='.info-text'>
                  <p sel='.text--caption-sm'>Launch, grow, and scale with no feature limits.</p>
                </div>
              </div>
              <div sel='.info-row'>
                <SvgIconEl icon={`checkmark`} className='checkmark-icon' />
                <div sel='.info-text'>
                  <p sel='.text--caption-sm'>Accelerate your product-market fit.</p>
                </div>
              </div>
              <h2 sel='.details-text.text--body-sm'>What are the details?</h2>
              <div sel='.info-row'>
                <SvgIconEl icon={`checkmark`} className='checkmark-icon' />
                <div sel='.info-text'>
                  <p sel='.text--caption-sm'>
                    Eligible startups enroll now to enjoy all the benefits of Mixpanel for free for the next year,
                    starting today!
                  </p>
                </div>
              </div>
              <div sel='.info-row'>
                <SvgIconEl icon={`checkmark`} className='checkmark-icon' />
                <div sel='.info-text'>
                  <p sel='.text--caption-sm'>
                    Must meet the following criteria:
                    <ul sel='.list--simple-text'>
                      <li>Haven’t been on a paid Mixpanel plan.</li>
                      <li>Founded less than 5 years ago.</li>
                      <li>Raised up to $8M USD in total funding.</li>
                      <li>Haven’t redeemed other Mixpanel offers.</li>
                      <li>
                        Start sending data to Mixpanel within 90 days of enrollment to maintain your spot in the
                        program.
                      </li>
                    </ul>
                  </p>
                </div>
              </div>
              {this.helpers?.isTFBF() && (
                <div sel='.info-row'>
                  <SvgIconEl icon={`checkmark`} className='checkmark-icon' />
                  <div sel='.info-text'>
                    <p sel='.text--caption-sm'>
                      Tech for Black Founders Eligibility Requirements
                      <ul sel='.list--simple-text'>
                        <li>Raised less than $30M in total funding</li>
                        <li>Employ fewer than 150 people</li>
                        <li>Haven't been on a paid plan</li>
                        <li>Led by one or more US-based Black founders</li>
                      </ul>
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div sel='.form-wrapper'>
            <div sel='.form-gradient'>
              <div sel='.form-border'>
                <form
                  attrs={{
                    id: `mpStartupsApply`,
                    class: `startups-apply-form-box`,
                    method: `post`,
                    action: `/startups-apply/?incubator=${encodeURIComponent(this.helpers?.getIncubator())}&next=`,
                  }}
                >
                  {eligible ? (
                    // We use the attr here because it disables changing your incubator
                    <>
                      <FormFields tfbf={this.attr(`tech-for-black-founders`)} />
                      <div sel='.form-button-row'>
                        <ButtonEl
                          sel='.auth-form-field-action-button'
                          attrs={{
                            disabled: this.helpers?.continueButtonDisabled(),
                            size: ButtonSize.Large,
                            theme: ButtonTheme.Primary,
                          }}
                          on={{click: this.helpers?.handleSubmitForm}}
                        >
                          Confirm Eligibility and Enroll
                        </ButtonEl>
                      </div>
                    </>
                  ) : (
                    <>
                      <div sel='.auth-form-field-content'>
                        {pastExpiry ? (
                          <>
                            <h1 sel='.text--body-sm'>{`Sorry! You aren't eligible.`}</h1>
                            <p sel='.text--caption-sm'>
                              You have already taken part in the Mixpanel for Startups program, which expired on{` `}
                              {this.attr(`expiration`)}. Have questions?
                              <a attrs={{href: `mailto:startups@mixpanel.com`, target: `_blank`, class: `mp-link`}}>
                                {` `}Contact our team.
                              </a>
                            </p>
                          </>
                        ) : hasOtherCredit ? (
                          <>
                            <h1 sel='.text--body-sm'>{`Sorry! You aren't eligible.`}</h1>
                            <p sel='.text--caption-sm'>
                              You have already received marketing credits. Per the
                              <a
                                attrs={{
                                  href: `https://docs.mixpanel.com/docs/pricing/startup-program#eligibility-faq`,
                                  target: `_blank`,
                                  class: `mp-link`,
                                }}
                              >
                                {` `}program guidelines
                              </a>
                              , startups are only able to redeem one offer. Have questions?
                              <a attrs={{href: `mailto:startups@mixpanel.com`, target: `_blank`, class: `mp-link`}}>
                                {` `}Contact our team.
                              </a>
                            </p>
                          </>
                        ) : canUpdateBilling ? (
                          <>
                            <h1 sel='.text--body-sm'>{`Sorry! You aren't eligible.`}</h1>
                            <p sel='.text--caption-sm'>
                              This plan is for new customers who{` `}
                              {currentPaid ? `haven’t already purchased a plan.` : `weren't previously on a paid plan.`}
                            </p>
                          </>
                        ) : (
                          <>
                            <h1 sel='.text--body-sm'>{`Sorry! You don’t have permission.`}</h1>
                            <p sel='.text--caption-sm'>
                              Please contact your organization’s billing admin or owner to make changes to your account.
                              If you think you’ve received this message in error, please
                              <a attrs={{href: `mailto:startups@mixpanel.com`, target: `_blank`, class: `mp-link`}}>
                                {` `}contact our team.
                              </a>
                            </p>
                          </>
                        )}
                        <div sel='.form-button-row'>
                          <a attrs={{href: `/`}}>
                            <ButtonEl
                              sel='.auth-form-field-action-button'
                              attrs={{size: ButtonSize.Large, theme: ButtonTheme.Primary}}
                            >
                              Back To Mixpanel
                            </ButtonEl>
                          </a>
                        </div>
                      </div>
                    </>
                  )}
                  {!loggedIn && (
                    <>
                      <div sel='.form-footer-row.last'>
                        <p sel='.text--caption-xs'>
                          Already using Mixpanel?
                          <a
                            attrs={{
                              href: this.helpers?.isTFBF()
                                ? `/login/?next=/startups-apply-tfbf/`
                                : `/login/?next=/startups-apply/`,
                              class: `mp-link`,
                            }}
                            style={{'padding-left': `5px`}}
                          >
                            Login first
                          </a>
                        </p>
                        <p sel='.text--caption-xs'>
                          Not ready or don't qualify? Try the
                          <a
                            attrs={{class: `mp-link`}}
                            style={{'padding-left': `5px`}}
                            on={{click: this.helpers?.clickFreePlan}}
                          >
                            Free plan first.
                          </a>
                        </p>
                      </div>
                    </>
                  )}
                </form>
              </div>
            </div>
          </div>
        </main>
      )}
    </div>
  );

  return <StartupsApplyLayout />;
}
